import { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { X, Share, PlusSquare, MoreVertical, Download, Monitor } from 'lucide-react';

const ONIRVA_ICON = "https://customer-assets.emergentagent.com/job_200ec0e0-0820-4417-b1b5-b6286eb18bc4/artifacts/c3nyk9fu_80AC8250-CA42-4BF9-BA0D-F1211AA6A764.png";

export default function AddToHomeScreen() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [platform, setPlatform] = useState(null); // 'ios', 'android', or 'desktop'
  const deferredPromptRef = useRef(null);
  
  useEffect(() => {
    const dismissed = localStorage.getItem('onirva_a2hs_dismissed');
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    
    if (dismissed || isStandalone) return;

    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    // iOS detection
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      const isSafari = /Safari/.test(userAgent) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(userAgent);
      if (isSafari) {
        const timer = setTimeout(() => {
          setPlatform('ios');
          setShowPrompt(true);
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
    // Android detection
    else if (/android/i.test(userAgent)) {
      const timer = setTimeout(() => {
        setPlatform('android');
        setShowPrompt(true);
      }, 3000);
      return () => clearTimeout(timer);
    }

    // Desktop: listen for the native beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setTimeout(() => {
        setPlatform('desktop');
        setShowPrompt(true);
      }, 3000);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Fallback: if no native prompt fires after 4s, show manual desktop instructions
    const fallbackTimer = setTimeout(() => {
      if (!deferredPromptRef.current && !showPrompt) {
        const isDesktop = !/android|ipad|iphone|ipod/i.test(userAgent);
        if (isDesktop) {
          setPlatform('desktop');
          setShowPrompt(true);
        }
      }
    }, 4000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      clearTimeout(fallbackTimer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const handleInstall = async () => {
    if (deferredPromptRef.current) {
      deferredPromptRef.current.prompt();
      const result = await deferredPromptRef.current.userChoice;
      if (result.outcome === 'accepted') {
        localStorage.setItem('onirva_a2hs_dismissed', 'true');
      }
      deferredPromptRef.current = null;
    }
    setShowPrompt(false);
    localStorage.setItem('onirva_a2hs_dismissed', 'true');
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('onirva_a2hs_dismissed', 'true');
  };
  
  const handleRemindLater = () => {
    setShowPrompt(false);
  };
  
  if (!showPrompt || !platform) return null;

  const hasNativePrompt = !!deferredPromptRef.current;
  
  return (
    <div data-testid="add-to-homescreen-overlay" className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div data-testid="add-to-homescreen-modal" className="w-full max-w-sm bg-[#13131a] border border-border rounded-2xl overflow-hidden animate-in slide-in-from-bottom duration-300">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <img src={ONIRVA_ICON} alt="ONIRVA" className="w-12 h-12 rounded-xl" />
            <div>
              <h3 className="font-serif font-bold text-lg">
                {platform === 'desktop' ? 'Install ONIRVA' : 'Add ONIRVA'}
              </h3>
              <p className="text-xs text-muted-foreground">
                {platform === 'desktop' ? 'on your desktop' : 'to your home screen'}
              </p>
            </div>
          </div>
          <button 
            data-testid="add-to-homescreen-close"
            onClick={handleDismiss}
            className="p-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4">
          <p className="text-sm text-muted-foreground mb-4">
            {platform === 'desktop'
              ? 'Install ONIRVA as a desktop app for quick access — no browser needed.'
              : 'Install ONIRVA for quick access and a better experience.'}
          </p>
          
          {/* iOS Instructions */}
          {platform === 'ios' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 text-sm font-medium text-primary">1</div>
                <p className="text-sm pt-1">
                  Tap the <span className="inline-flex items-center mx-1 px-2 py-0.5 bg-muted rounded"><Share className="w-4 h-4" /></span> Share button at the bottom of Safari
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 text-sm font-medium text-primary">2</div>
                <p className="text-sm pt-1">
                  Scroll and tap <span className="inline-flex items-center mx-1 px-2 py-0.5 bg-muted rounded"><PlusSquare className="w-4 h-4 mr-1" /> Add to Home Screen</span>
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 text-sm font-medium text-primary">3</div>
                <p className="text-sm pt-1">
                  Tap <span className="font-medium text-primary">Add</span> in the top right corner
                </p>
              </div>
            </div>
          )}
          
          {/* Android Instructions */}
          {platform === 'android' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 text-sm font-medium text-primary">1</div>
                <p className="text-sm pt-1">
                  Tap the <span className="inline-flex items-center mx-1 px-2 py-0.5 bg-muted rounded"><MoreVertical className="w-4 h-4" /></span> menu button in your browser
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 text-sm font-medium text-primary">2</div>
                <p className="text-sm pt-1">
                  Tap <span className="inline-flex items-center mx-1 px-2 py-0.5 bg-muted rounded">Add to Home screen</span> or <span className="inline-flex items-center mx-1 px-2 py-0.5 bg-muted rounded">Install app</span>
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 text-sm font-medium text-primary">3</div>
                <p className="text-sm pt-1">
                  Tap <span className="font-medium text-primary">Add</span> or <span className="font-medium text-primary">Install</span> to confirm
                </p>
              </div>
            </div>
          )}

          {/* Desktop Instructions */}
          {platform === 'desktop' && !hasNativePrompt && (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 text-sm font-medium text-primary">1</div>
                <p className="text-sm pt-1">
                  Look for the <span className="inline-flex items-center mx-1 px-2 py-0.5 bg-muted rounded"><Download className="w-4 h-4 mr-1" /> Install</span> icon in your browser's address bar
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 text-sm font-medium text-primary">2</div>
                <p className="text-sm pt-1">
                  Or open the <span className="inline-flex items-center mx-1 px-2 py-0.5 bg-muted rounded"><MoreVertical className="w-4 h-4" /></span> browser menu and select <span className="font-medium text-primary">Install ONIRVA</span>
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 text-sm font-medium text-primary">3</div>
                <p className="text-sm pt-1 flex items-center gap-1">
                  ONIRVA will open as its own app <Monitor className="w-4 h-4 inline text-primary" /> — no browser tabs needed
                </p>
              </div>
            </div>
          )}

          {/* Desktop with native prompt — simpler message */}
          {platform === 'desktop' && hasNativePrompt && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/10">
              <Monitor className="w-8 h-8 text-primary flex-shrink-0" />
              <p className="text-sm">
                Click <span className="font-medium text-primary">Install</span> below to add ONIRVA to your desktop. It will open as its own window — no browser needed.
              </p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 pt-0 flex gap-2">
          <Button 
            data-testid="add-to-homescreen-later"
            variant="ghost" 
            className="flex-1"
            onClick={handleRemindLater}
          >
            Maybe Later
          </Button>
          {platform === 'desktop' && hasNativePrompt ? (
            <Button 
              data-testid="add-to-homescreen-install"
              className="flex-1 rounded-full"
              onClick={handleInstall}
            >
              <Download className="w-4 h-4 mr-2" /> Install
            </Button>
          ) : (
            <Button 
              data-testid="add-to-homescreen-gotit"
              className="flex-1 rounded-full"
              onClick={handleDismiss}
            >
              Got It
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
